import React, { Fragment, useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Collapse,
  DialogContentText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import ViewConsumerLoadDetails from "./ViewConsumerLoadDetails";
import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useGetUtilityConsumerLoadTrackingDetails } from "apis/UtilityConsumer/utilityConsumer";

const UtilityConsumerLoadTracking = ({ utilityConsumerId, handleClose, t }) => {
  const { data: { data: utilityConsumerLoadTrackingDetails } = {}, isLoading } =
    useGetUtilityConsumerLoadTrackingDetails(utilityConsumerId);

  const [selectedLoadDetailId, setSelectedLoadDetailId] = useState("");

  const selectedLoadDetails = useMemo(
    () =>
      utilityConsumerLoadTrackingDetails?.find(
        ({ id }) => id === selectedLoadDetailId
      ) ?? {},
    [utilityConsumerLoadTrackingDetails, selectedLoadDetailId]
  );

  const handleToggle = (id) => {
    if (selectedLoadDetailId === id) {
      setSelectedLoadDetailId("");
    } else {
      setSelectedLoadDetailId(id);
    }
  };

  useEffect(() => {
    if (utilityConsumerLoadTrackingDetails?.length === 1) {
      setSelectedLoadDetailId(utilityConsumerLoadTrackingDetails[0].id);
    }
  }, [utilityConsumerLoadTrackingDetails]);

  if (isLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Consumer's Updated Details"
      maxWidth="lg"
    >
      {utilityConsumerLoadTrackingDetails?.length ? (
        utilityConsumerLoadTrackingDetails.map(({ id, timeStamp, remarks }) => (
          <Fragment key={id}>
            <div
              className="fs-4 fw-semibold mb-3 border-bottom"
              onClick={() => handleToggle(id)}
              key={id}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className="cursor-pointer"
              >
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center gap-1">
                    <p className="fw-bold mb-0">Updated At: </p>
                    <p className="fw-light fs-5 mb-0">
                      {convertUTCDateToLocalDate(timeStamp).format(
                        "DD-MM-YYYY HH:mm:ss A"
                      )}
                    </p>
                  </div>
                  <div className="d-flex  align-items-center gap-1">
                    <p className="fw-bold mb-0">Remarks: </p>
                    <p className="fw-light fs-5 mb-0">{remarks}</p>
                  </div>
                </div>
                <IconButton>
                  {selectedLoadDetailId === id ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </IconButton>
              </Stack>
            </div>
            <DialogContentText variant="subtitle2" component="div">
              <Collapse
                in={selectedLoadDetailId === id}
                timeout="auto"
                unmountOnExit
              >
                <>
                  <ViewConsumerLoadDetails
                    title="Consumer's Old Details"
                    loadDetails={selectedLoadDetails.oldConsumerDetails}
                  />

                  <ViewConsumerLoadDetails
                    title="Consumer's New Details"
                    loadDetails={selectedLoadDetails.newConsumerDetails}
                  />
                </>
              </Collapse>
            </DialogContentText>
          </Fragment>
        ))
      ) : (
        <Typography variant="h6" className="text-center mt-3">
          {t("This consumer's details has not been updated till now")}
        </Typography>
      )}
    </Modal>
  );
};

UtilityConsumerLoadTracking.propTypes = {
  utilityConsumerId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(UtilityConsumerLoadTracking);
